import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { flushSync } from 'react-dom'

/** Three modes: explicit light/dark, or `system` = follow the OS/browser theme. */
export type ThemeMode = 'light' | 'dark' | 'system'

const KEY = 'aeys-vco-theme'
const MODES: ThemeMode[] = ['light', 'dark', 'system']
/** Present on <html> only while a swap is cross-fading — see swap() below. */
const SWAPPING = 'data-theme-swapping'

function readStored(): ThemeMode {
  try {
    const v = localStorage.getItem(KEY)
    if (v && MODES.includes(v as ThemeMode)) return v as ThemeMode
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return 'system'
}

/**
 * Reflect the mode onto <html>. `system` removes the attribute so the CSS
 * `@media (prefers-color-scheme)` fallback governs; explicit modes stamp
 * `data-theme` so they win over the OS preference. Mirrors the inline
 * boot script in index.html that runs this before first paint (no FOUC).
 *
 * The attribute is the only thing set here: the page background and
 * `color-scheme` both hang off it in CSS — in index.css, and in the
 * first-paint block in index.html that stands in until index.css loads.
 */
function apply(mode: ThemeMode) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (mode === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', mode)
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Change the theme by cross-fading the whole page, not by interpolating every
 * colour independently.
 *
 * Independent interpolation is what made headings blink out mid-swap: ink runs
 * #2a2a24 -> #ecebe5 while paper runs #f6f3ec -> #16171a, and halfway through
 * both sit on the same grey — a measured 1.08:1, i.e. invisible, for about
 * 60ms. No pair of easing curves avoids that crossing; the two colours have to
 * pass each other.
 *
 * A view transition sidesteps it: the browser fades a snapshot of the old page
 * into the new one, and each snapshot keeps its own internal contrast the whole
 * way. The CSS property transitions are suppressed while it runs (the new view
 * is live, so they would otherwise re-introduce the crossing inside it), and
 * where the API or motion preference says no, the swap is simply instant.
 */
function swap(commit: () => void) {
  // Chromium-only today, so the capability check is the feature detection as
  // well as the SSR guard.
  const start = typeof document === 'undefined' ? undefined : document.startViewTransition
  if (!start || prefersReducedMotion()) {
    commit()
    return
  }
  const root = document.documentElement
  root.setAttribute(SWAPPING, '')
  const transition = start.call(document, commit)
  // `finished` rejects when a swap is abandoned — usually by the next one. The
  // attribute has to come off either way, or transitions stay suppressed for
  // the rest of the session.
  void Promise.resolve(transition.finished)
    .catch(() => {})
    .finally(() => root.removeAttribute(SWAPPING))
}

const Ctx = createContext<{ mode: ThemeMode; setMode: (m: ThemeMode) => void }>({
  mode: 'system',
  setMode: () => {},
})

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStored)

  useEffect(() => {
    apply(mode)
    try {
      localStorage.setItem(KEY, mode)
    } catch {
      /* ignore persistence failures */
    }
  }, [mode])

  // flushSync commits the React side — the toggle's own active state — before
  // the browser captures the new view, which it does the moment this callback
  // returns. It flushes the effect below with it, so `apply` here is belt and
  // braces rather than load-bearing: the capture is a visual-correctness
  // property, and it should not rest on flushSync's effect timing holding.
  const setMode = (next: ThemeMode) =>
    swap(() => {
      apply(next)
      flushSync(() => setModeState(next))
    })

  return <Ctx.Provider value={{ mode, setMode }}>{children}</Ctx.Provider>
}

export const useTheme = () => useContext(Ctx)
